import React, { useState, useEffect } from 'react';

interface WebVideoPlayerProps {
  onVideoSelect: (videoUrl: string) => void;
}

interface VideoSource {
  url: string;
  label: string;
  type: 'video' | 'iframe';
  quality?: string;
}

const WebVideoPlayer: React.FC<WebVideoPlayerProps> = ({ onVideoSelect }) => {
  const [siteUrl, setSiteUrl] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [detectedVideos, setDetectedVideos] = useState<VideoSource[]>([]);
  const [isDetecting, setIsDetecting] = useState(false);

  // Función para detectar videos en una página web
  const detectVideos = async (url: string) => {
    setIsDetecting(true);
    setError(null);
    setDetectedVideos([]);
    
    try {
      // Simular la detección de videos
      // En un entorno real, esto requeriría una API en el backend debido a restricciones CORS
      console.log('Detectando videos en:', url);
      
      // Verificar si es una URL válida
      if (!isValidUrl(url)) {
        throw new Error('La URL no parece ser válida. Por favor, verifica el enlace.');
      }
      
      // Esta es una simulación - normalmente aquí haríamos una llamada a un backend
      // que analizaría la página y extraería las fuentes de video
      const fakeDetectionDelay = Math.random() * 1000 + 1000; // 1-2 segundos de retardo simulado
      
      await new Promise(resolve => setTimeout(resolve, fakeDetectionDelay));
      
      // Generar fuentes simuladas basadas en la URL
      let simulatedSources: VideoSource[] = [];
      
      // OtakusTV y sitios de anime
      if (isAnimeSite(url)) {
        simulatedSources = generateAnimeSourcesForSite(url);
      } 
      // Sitios generales
      else {
        simulatedSources = generateGenericSourcesForSite(url);
      }
      
      if (simulatedSources.length === 0) {
        throw new Error('No se detectaron fuentes de video en esta página. Intenta con otra URL o selecciona manualmente.');
      }
      
      setDetectedVideos(simulatedSources);
    } catch (err: any) {
      setError(err.message || 'Error al detectar videos');
    } finally {
      setIsDetecting(false);
    }
  };

  // Función para validar URLs
  const isValidUrl = (url: string): boolean => {
    try {
      new URL(url);
      return true;
    } catch {
      return false;
    }
  };

  // Función para detectar si es un sitio de anime
  const isAnimeSite = (url: string): boolean => {
    const animeDomains = [
      'otakustv.com', 'otakus.tv', 'otakustv',
      'animeflv', 'jkanime', 'animefenix',
      'crunchyroll', 'funimation', 'aniplus',
      'tioanime', 'animeblix', 'animeyt'
    ];
    
    return animeDomains.some(domain => url.includes(domain));
  };

  // Función para generar fuentes simuladas para sitios de anime
  const generateAnimeSourcesForSite = (url: string): VideoSource[] => {
    // Extraer el dominio para personalizar la respuesta
    let domain = '';
    try {
      domain = new URL(url).hostname;
    } catch {
      domain = url.split('/')[0];
    }
    
    // Simular diferentes servidores según el sitio
    const sources: VideoSource[] = [];
    
    // Simular múltiples servidores que típicamente ofrecen los sitios de anime
    const servers = ['Fembed', 'Streamtape', 'Streamwish', 'Voe', 'Embedsito'];
    const qualities = ['720p', '1080p', '480p'];
    
    servers.forEach((server, index) => {
      sources.push({
        url: `${url}${url.includes('?') ? '&' : '?'}server=${server.toLowerCase()}`,
        label: `${server} ${qualities[index % qualities.length]}`,
        type: 'iframe',
        quality: qualities[index % qualities.length]
      });
    });
    
    return sources;
  };

  // Función para generar fuentes simuladas para sitios genéricos
  const generateGenericSourcesForSite = (url: string): VideoSource[] => {
    // Extraer el dominio para personalizar la respuesta
    let domain = '';
    try {
      domain = new URL(url).hostname;
    } catch {
      domain = url.split('/')[0];
    }
    
    const sources: VideoSource[] = [];
    
    // Simular diferentes calidades de video
    const qualities = ['1080p', '720p', '480p', '360p'];
    
    qualities.forEach((quality, index) => {
      sources.push({
        url: `${url}${url.includes('?') ? '&' : '?'}quality=${quality.replace('p', '')}`,
        label: `Video ${quality}`,
        type: 'video',
        quality
      });
    });
    
    // Añadir una opción de reproductor embebido si no es un sitio de anime
    sources.push({
      url,
      label: 'Reproductor original',
      type: 'iframe'
    });
    
    return sources;
  };

  // Manejar la URL enviada para detectar videos
  const handleUrlSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!siteUrl.trim()) {
      setError('Por favor, ingresa una URL');
      return;
    }

    setLoading(true);
    setError(null);
    detectVideos(siteUrl);
    setLoading(false);
  };

  // Manejar la selección de un video
  const handleVideoSelect = (videoSource: VideoSource) => {
    console.log('Video seleccionado:', videoSource);
    onVideoSelect(videoSource.url);
  };

  // Efecto para detectar videos automáticamente cuando se pega una URL
  useEffect(() => {
    if (siteUrl && isValidUrl(siteUrl) && !isDetecting && !loading) {
      const debounceTimer = setTimeout(() => {
        detectVideos(siteUrl);
      }, 1000); // Esperar 1 segundo después de que el usuario deje de escribir
      
      return () => clearTimeout(debounceTimer);
    }
  }, [siteUrl]);

  return (
    <div className="p-4 sm:p-6">
      <h3 className="text-lg sm:text-xl font-medium mb-4 text-center" style={{ color: 'var(--text-color)' }}>
        Reproductor de Video Web
      </h3>
      
      {error && (
        <div className="mb-4 p-3 bg-red-500/20 text-red-400 rounded-lg text-sm">
          {error}
        </div>
      )}
      
      <form onSubmit={handleUrlSubmit} className="space-y-4">
        <div>
          <label className="block text-sm font-medium mb-2" style={{ color: 'var(--text-secondary)' }}>
            Pega la URL del sitio con videos
          </label>
          <input
            type="url"
            value={siteUrl}
            onChange={(e) => setSiteUrl(e.target.value)}
            placeholder="https://example.com/video-page"
            className="themed-input w-full px-4 py-2 rounded-lg"
          />
        </div>
        
        <div className="flex justify-between items-center">
          <div className="text-sm" style={{ color: 'var(--text-secondary)' }}>
            {isDetecting ? 'Detectando videos...' : 'Se detectarán los videos automáticamente'}
          </div>
          
          <button
            type="submit"
            disabled={isDetecting || !siteUrl.trim()}
            className="themed-button px-4 py-2 rounded-lg flex items-center space-x-2"
          >
            {isDetecting ? (
              <>
                <svg className="animate-spin h-5 w-5" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
                <span>Detectando</span>
              </>
            ) : (
              <>
                <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                </svg>
                <span>Detectar videos</span>
              </>
            )}
          </button>
        </div>
      </form>
      
      {/* Lista de videos detectados */}
      {detectedVideos.length > 0 && (
        <div className="mt-6">
          <h4 className="font-medium mb-3 text-gray-200">Videos detectados ({detectedVideos.length})</h4>
          <div className="space-y-2 max-h-60 overflow-y-auto pr-2 custom-scrollbar">
            {detectedVideos.map((video, index) => (
              <div 
                key={index}
                onClick={() => handleVideoSelect(video)}
                className="p-3 bg-gray-800/50 hover:bg-gray-700/50 rounded-lg cursor-pointer transition-colors flex items-center justify-between"
              >
                <div className="flex items-center">
                  <div className="bg-blue-500/20 rounded-full p-2 mr-3">
                    {video.type === 'video' ? (
                      <svg className="h-4 w-4 text-blue-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M14.752 11.168l-3.197-2.132A1 1 0 0010 9.87v4.263a1 1 0 001.555.832l3.197-2.132a1 1 0 000-1.664z" />
                      </svg>
                    ) : (
                      <svg className="h-4 w-4 text-blue-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                      </svg>
                    )}
                  </div>
                  <div>
                    <div className="text-sm text-gray-200">{video.label}</div>
                    <div className="text-xs text-gray-400">{video.type === 'video' ? 'Video directo' : 'Reproductor embebido'}</div>
                  </div>
                </div>
                
                {video.quality && (
                  <span className="inline-block px-2 py-1 bg-blue-500/10 text-blue-400 rounded text-xs">
                    {video.quality}
                  </span>
                )}
              </div>
            ))}
          </div>
        </div>
      )}
      
      <div className="mt-6 p-4 bg-blue-500/10 rounded-lg">
        <h4 className="font-medium mb-2 text-blue-400">Consejos:</h4>
        <ul className="list-disc list-inside text-sm space-y-1" style={{ color: 'var(--text-secondary)' }}>
          <li>Funciona con sitios de videos, streaming y anime</li>
          <li>Los videos se detectan automáticamente al pegar la URL</li>
          <li>Si no se detectan videos, intenta con otro enlace o servidor</li>
          <li>Para mejor experiencia, elige la calidad según tu conexión a internet</li>
        </ul>
      </div>
    </div>
  );
};

export default WebVideoPlayer; 